import React, { useState, useEffect, useCallback } from 'react';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { ArrowLeft, ClipboardPaste, ListOrdered, Undo2, ShieldCheck } from 'lucide-react';
import { ABLE_COLORS } from '../../../constants';
import { useToast } from '../../Toast';
import {
  parseImport,
  confirmImport,
  undoImport,
  getTransactions,
  updateTransaction,
  getCategories,
  createCategory,
  getCurrentMonth,
  formatKr
} from '../../../utils/moneyApi';

/**
 * MoneyView - Bills, budget, and transactions
 *
 * v1 tabs: Import (paste statement) and Transactions.
 * Self-contained: manages its own data via moneyApi, no prop drilling.
 *
 * Safety: paste-only. No bank connections. Nothing saved without confirm.
 */
export default function MoneyView({ onBack }) {
  const { getToken, isAuthenticated } = useKindeAuth();
  const { showToast } = useToast();

  const [tab, setTab] = useState('import');
  const [categories, setCategories] = useState([]);

  const loadCategories = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const token = await getToken();
      const result = await getCategories(token);
      if (result.success) setCategories(result.categories || []);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  }, [isAuthenticated, getToken]);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  const handleCreateCategory = async (name, type) => {
    try {
      const token = await getToken();
      const result = await createCategory({ name, type }, token);
      if (result.success) {
        await loadCategories();
        return result.id;
      }
    } catch (err) {
      showToast(`Could not create category: ${err.message}`, 'error');
    }
    return null;
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
        <button
          onClick={onBack}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: '8px', display: 'flex', alignItems: 'center'
          }}
          aria-label="Back"
        >
          <ArrowLeft size={20} color={ABLE_COLORS.textPrimary} />
        </button>
        <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: ABLE_COLORS.textPrimary }}>
          Money
        </h2>
      </div>

      {/* Safety note */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '8px',
        padding: '8px 12px', marginBottom: '16px',
        background: ABLE_COLORS.neutral,
        border: `1px solid ${ABLE_COLORS.border}`,
        borderRadius: ABLE_COLORS.cardRadius,
        fontSize: '12px', color: ABLE_COLORS.textMuted
      }}>
        <ShieldCheck size={16} color={ABLE_COLORS.primary} />
        <span>Paste-only. Mabel never connects to your bank. Nothing is saved until you confirm.</span>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        {[
          { id: 'import', label: 'Import', icon: ClipboardPaste },
          { id: 'transactions', label: 'Transactions', icon: ListOrdered }
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '8px 16px',
              background: tab === t.id ? ABLE_COLORS.primary : ABLE_COLORS.neutral,
              color: tab === t.id ? '#fff' : ABLE_COLORS.textPrimary,
              border: `1px solid ${tab === t.id ? ABLE_COLORS.primary : ABLE_COLORS.border}`,
              borderRadius: '20px',
              fontSize: '14px', fontWeight: 600, cursor: 'pointer'
            }}
          >
            <t.icon size={16} />
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'import' && (
        <ImportTab
          getToken={getToken}
          categories={categories}
          onCreateCategory={handleCreateCategory}
          showToast={showToast}
        />
      )}
      {tab === 'transactions' && (
        <TransactionsTab
          getToken={getToken}
          categories={categories}
          showToast={showToast}
        />
      )}
    </div>
  );
}

/* ============ IMPORT TAB ============ */

function ImportTab({ getToken, categories, onCreateCategory, showToast }) {
  const [text, setText] = useState('');
  const [preview, setPreview] = useState(null); // { rows, skipped, stats }
  const [rowState, setRowState] = useState([]); // per-row: category_id, remember, include
  const [isParsing, setIsParsing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastBatch, setLastBatch] = useState(null); // { batchId, inserted }

  const handleParse = async () => {
    if (!text.trim()) return;
    setIsParsing(true);
    setPreview(null);
    try {
      const token = await getToken();
      const result = await parseImport(text, token);
      if (result.success) {
        setPreview(result);
        setRowState((result.rows || []).map(row => ({
          category_id: row.category_id || '',
          remember: false,
          include: !row.duplicate
        })));
        if ((result.rows || []).length === 0) {
          showToast('No transaction rows found in the pasted text', 'error');
        }
      }
    } catch (err) {
      showToast(`Parse failed: ${err.message}`, 'error');
    } finally {
      setIsParsing(false);
    }
  };

  const setRow = (i, updates) => {
    setRowState(prev => prev.map((r, idx) => idx === i ? { ...r, ...updates } : r));
  };

  const handleConfirm = async () => {
    if (!preview) return;
    const rowsToSave = [];
    const newRules = [];
    const seenRules = new Set();

    preview.rows.forEach((row, i) => {
      const state = rowState[i];
      if (!state.include) return;
      rowsToSave.push({
        booking_date: row.booking_date,
        transaction_date: row.transaction_date,
        description: row.description,
        amount: row.amount,
        balance: row.balance,
        category_id: state.category_id || null,
        bill_id: row.bill_id || null
      });
      if (state.remember && state.category_id) {
        // Rule matches on the merchant part before the first comma, or full text
        const matchText = row.description.split(',')[0].trim() || row.description;
        const key = `${matchText}|${state.category_id}`;
        if (!seenRules.has(key)) {
          seenRules.add(key);
          newRules.push({ match_text: matchText, category_id: state.category_id });
        }
      }
    });

    if (rowsToSave.length === 0) {
      showToast('No rows selected to save', 'error');
      return;
    }

    setIsSaving(true);
    try {
      const token = await getToken();
      const result = await confirmImport(rowsToSave, newRules, token);
      if (result.success) {
        setLastBatch({ batchId: result.batchId, inserted: result.inserted });
        setPreview(null);
        setRowState([]);
        setText('');
        const ruleNote = result.rulesCreated > 0 ? `, ${result.rulesCreated} rules remembered` : '';
        showToast(`Saved ${result.inserted} transactions${ruleNote}`, 'success');
      }
    } catch (err) {
      showToast(`Save failed: ${err.message}. Nothing was saved.`, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUndo = async () => {
    if (!lastBatch) return;
    try {
      const token = await getToken();
      const result = await undoImport(lastBatch.batchId, token);
      if (result.success) {
        showToast(`Removed ${result.deleted} transactions`, 'success');
        setLastBatch(null);
      }
    } catch (err) {
      showToast(`Undo failed: ${err.message}`, 'error');
    }
  };

  const inputStyle = {
    width: '100%', boxSizing: 'border-box',
    padding: '12px', fontSize: '13px',
    fontFamily: 'monospace',
    border: `1px solid ${ABLE_COLORS.border}`,
    borderRadius: ABLE_COLORS.cardRadius,
    background: ABLE_COLORS.neutral,
    color: ABLE_COLORS.textPrimary,
    resize: 'vertical'
  };

  return (
    <div>
      {/* Undo banner for last saved batch */}
      {lastBatch && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 12px', marginBottom: '12px',
          background: ABLE_COLORS.neutral,
          border: `1px solid ${ABLE_COLORS.border}`,
          borderRadius: ABLE_COLORS.cardRadius,
          fontSize: '13px', color: ABLE_COLORS.textPrimary
        }}>
          <span>Saved {lastBatch.inserted} transactions</span>
          <button
            onClick={handleUndo}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '6px 12px', background: 'none',
              border: `1px solid ${ABLE_COLORS.border}`,
              borderRadius: '16px', cursor: 'pointer',
              fontSize: '12px', color: ABLE_COLORS.textPrimary
            }}
          >
            <Undo2 size={14} /> Undo this import
          </button>
        </div>
      )}

      {!preview && (
        <>
          <p style={{ fontSize: '13px', color: ABLE_COLORS.textMuted, marginTop: 0 }}>
            Open your bank statement in Excel, select the rows (with or without headers, both work), copy, and paste here.
          </p>
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder={'2026-06-01\t2026-06-01\tNETFLIX.COM,AMSTERDAM,NL\t-149,00\t10 114,24'}
            rows={10}
            style={inputStyle}
          />
          <button
            onClick={handleParse}
            disabled={isParsing || !text.trim()}
            style={{
              marginTop: '12px', padding: '10px 20px',
              background: ABLE_COLORS.primary, color: '#fff',
              border: 'none', borderRadius: '20px',
              fontSize: '14px', fontWeight: 600,
              cursor: text.trim() ? 'pointer' : 'not-allowed',
              opacity: isParsing || !text.trim() ? 0.6 : 1
            }}
          >
            {isParsing ? 'Reading...' : 'Preview'}
          </button>
        </>
      )}

      {preview && preview.rows.length > 0 && (
        <>
          <div style={{ fontSize: '13px', color: ABLE_COLORS.textMuted, marginBottom: '12px' }}>
            {preview.stats.new} new, {preview.stats.duplicates} already imported (unchecked below).
            Nothing is saved until you confirm.
          </div>

          <PreviewTable
            rows={preview.rows}
            rowState={rowState}
            setRow={setRow}
            categories={categories}
            onCreateCategory={onCreateCategory}
          />

          <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
            <button
              onClick={handleConfirm}
              disabled={isSaving}
              style={{
                padding: '10px 20px',
                background: ABLE_COLORS.primary, color: '#fff',
                border: 'none', borderRadius: '20px',
                fontSize: '14px', fontWeight: 600, cursor: 'pointer',
                opacity: isSaving ? 0.6 : 1
              }}
            >
              {isSaving ? 'Saving...' : `Save ${rowState.filter(r => r.include).length} transactions`}
            </button>
            <button
              onClick={() => { setPreview(null); setRowState([]); }}
              disabled={isSaving}
              style={{
                padding: '10px 20px',
                background: 'none', color: ABLE_COLORS.textPrimary,
                border: `1px solid ${ABLE_COLORS.border}`,
                borderRadius: '20px',
                fontSize: '14px', fontWeight: 600, cursor: 'pointer'
              }}
            >
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/* ============ PREVIEW TABLE ============ */

function PreviewTable({ rows, rowState, setRow, categories, onCreateCategory }) {
  const [newCatFor, setNewCatFor] = useState(null); // row index showing inline create
  const [newCatName, setNewCatName] = useState('');
  const [newCatType, setNewCatType] = useState('variable');

  const handleInlineCreate = async (rowIndex) => {
    if (!newCatName.trim()) return;
    const id = await onCreateCategory(newCatName.trim(), newCatType);
    if (id) {
      setRow(rowIndex, { category_id: id });
      setNewCatFor(null);
      setNewCatName('');
    }
  };

  const cellStyle = {
    padding: '8px', fontSize: '13px',
    borderBottom: `1px solid ${ABLE_COLORS.border}`,
    color: ABLE_COLORS.textPrimary, verticalAlign: 'middle'
  };

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '640px' }}>
        <thead>
          <tr>
            {['', 'Date', 'Description', 'Amount', 'Category', 'Remember'].map(h => (
              <th key={h} style={{
                ...cellStyle, textAlign: 'left', fontSize: '12px',
                color: ABLE_COLORS.textMuted, fontWeight: 600
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const state = rowState[i] || {};
            return (
              <tr key={i} style={{ opacity: state.include ? 1 : 0.45 }}>
                <td style={cellStyle}>
                  <input
                    type="checkbox"
                    checked={!!state.include}
                    onChange={e => setRow(i, { include: e.target.checked })}
                    title={row.duplicate ? 'Already imported' : 'Include'}
                  />
                </td>
                <td style={{ ...cellStyle, whiteSpace: 'nowrap' }}>{row.booking_date}</td>
                <td style={{ ...cellStyle, maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.description}>
                  {row.description}
                  {row.duplicate && (
                    <span style={{ marginLeft: '6px', fontSize: '11px', color: ABLE_COLORS.textMuted }}>
                      (duplicate)
                    </span>
                  )}
                </td>
                <td style={{
                  ...cellStyle, whiteSpace: 'nowrap', textAlign: 'right',
                  color: row.amount > 0 ? ABLE_COLORS.primary : ABLE_COLORS.textPrimary,
                  fontVariantNumeric: 'tabular-nums'
                }}>
                  {formatKr(row.amount)}
                </td>
                <td style={cellStyle}>
                  {newCatFor === i ? (
                    <span style={{ display: 'inline-flex', gap: '4px', alignItems: 'center' }}>
                      <input
                        autoFocus
                        value={newCatName}
                        onChange={e => setNewCatName(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleInlineCreate(i); if (e.key === 'Escape') setNewCatFor(null); }}
                        placeholder="Name"
                        style={{
                          width: '110px', padding: '4px 6px', fontSize: '12px',
                          border: `1px solid ${ABLE_COLORS.border}`, borderRadius: '6px'
                        }}
                      />
                      <select
                        value={newCatType}
                        onChange={e => setNewCatType(e.target.value)}
                        style={{ fontSize: '12px', padding: '4px' }}
                      >
                        <option value="variable">Variable</option>
                        <option value="fixed">Fixed</option>
                        <option value="income">Income</option>
                      </select>
                      <button onClick={() => handleInlineCreate(i)} style={{ fontSize: '12px', cursor: 'pointer' }}>Add</button>
                    </span>
                  ) : (
                    <select
                      value={state.category_id || ''}
                      onChange={e => {
                        if (e.target.value === '__new__') { setNewCatFor(i); }
                        else setRow(i, { category_id: e.target.value });
                      }}
                      style={{
                        fontSize: '12px', padding: '4px 6px',
                        border: `1px solid ${ABLE_COLORS.border}`, borderRadius: '6px',
                        background: state.category_id ? ABLE_COLORS.neutral : 'transparent',
                        maxWidth: '150px'
                      }}
                    >
                      <option value="">Uncategorized</option>
                      {categories.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                      <option value="__new__">+ New category</option>
                    </select>
                  )}
                </td>
                <td style={{ ...cellStyle, textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    checked={!!state.remember}
                    disabled={!state.category_id}
                    onChange={e => setRow(i, { remember: e.target.checked })}
                    title={state.category_id ? 'Remember this categorization for next time' : 'Pick a category first'}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ============ TRANSACTIONS TAB ============ */

function TransactionsTab({ getToken, categories, showToast }) {
  const [month, setMonth] = useState(getCurrentMonth());
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = await getToken();
      const result = await getTransactions({ month }, token);
      if (result.success) setTransactions(result.transactions || []);
    } catch (err) {
      showToast(`Failed to load transactions: ${err.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [getToken, month, showToast]);

  useEffect(() => { load(); }, [load]);

  const handleRecategorize = async (tx, categoryId) => {
    try {
      const token = await getToken();
      await updateTransaction(tx.id, { category_id: categoryId || null, bill_id: tx.bill_id }, token);
      setTransactions(prev => prev.map(t => t.id === tx.id ? { ...t, category_id: categoryId || null } : t));
    } catch (err) {
      showToast(`Update failed: ${err.message}`, 'error');
    }
  };

  const changeMonth = (delta) => {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const totalIn = transactions.filter(t => Number(t.amount) > 0).reduce((s, t) => s + Number(t.amount), 0);
  const totalOut = transactions.filter(t => Number(t.amount) < 0).reduce((s, t) => s + Number(t.amount), 0);

  const cellStyle = {
    padding: '8px', fontSize: '13px',
    borderBottom: `1px solid ${ABLE_COLORS.border}`,
    color: ABLE_COLORS.textPrimary
  };

  return (
    <div>
      {/* Month navigation */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
        <button onClick={() => changeMonth(-1)} style={{ padding: '6px 12px', cursor: 'pointer', border: `1px solid ${ABLE_COLORS.border}`, borderRadius: '8px', background: ABLE_COLORS.neutral }}>&larr;</button>
        <span style={{ fontSize: '15px', fontWeight: 600, color: ABLE_COLORS.textPrimary, minWidth: '80px', textAlign: 'center' }}>{month}</span>
        <button onClick={() => changeMonth(1)} style={{ padding: '6px 12px', cursor: 'pointer', border: `1px solid ${ABLE_COLORS.border}`, borderRadius: '8px', background: ABLE_COLORS.neutral }}>&rarr;</button>
        <span style={{ marginLeft: 'auto', fontSize: '13px', color: ABLE_COLORS.textMuted }}>
          In {formatKr(totalIn)} &middot; Out {formatKr(totalOut)}
        </span>
      </div>

      {isLoading && <div style={{ fontSize: '13px', color: ABLE_COLORS.textMuted }}>Loading...</div>}

      {!isLoading && transactions.length === 0 && (
        <div style={{ fontSize: '13px', color: ABLE_COLORS.textMuted }}>
          No transactions for this month yet. Paste a statement in the Import tab.
        </div>
      )}

      {!isLoading && transactions.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '560px' }}>
            <thead>
              <tr>
                {['Date', 'Description', 'Amount', 'Category'].map(h => (
                  <th key={h} style={{ ...cellStyle, textAlign: 'left', fontSize: '12px', color: ABLE_COLORS.textMuted, fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {transactions.map(tx => (
                <tr key={tx.id}>
                  <td style={{ ...cellStyle, whiteSpace: 'nowrap' }}>{String(tx.booking_date).slice(0, 10)}</td>
                  <td style={{ ...cellStyle, maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={tx.description}>
                    {tx.description}
                  </td>
                  <td style={{
                    ...cellStyle, whiteSpace: 'nowrap', textAlign: 'right',
                    color: Number(tx.amount) > 0 ? ABLE_COLORS.primary : ABLE_COLORS.textPrimary,
                    fontVariantNumeric: 'tabular-nums'
                  }}>
                    {formatKr(tx.amount)}
                  </td>
                  <td style={cellStyle}>
                    <select
                      value={tx.category_id || ''}
                      onChange={e => handleRecategorize(tx, e.target.value)}
                      style={{
                        fontSize: '12px', padding: '4px 6px',
                        border: `1px solid ${ABLE_COLORS.border}`, borderRadius: '6px',
                        background: tx.category_id ? ABLE_COLORS.neutral : 'transparent',
                        maxWidth: '150px'
                      }}
                    >
                      <option value="">Uncategorized</option>
                      {categories.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
